/**
 * PROJECT COMMAND — what the first screen is allowed to say.
 *
 * The dashboard is the first thing a paying contractor sees, and until this
 * module existed it told them three things that were not true:
 *
 *   1. a six-month "Budget vs Actual Spend" curve drawn from six numbers
 *      hardcoded in the page (DashboardPage.jsx, `budgetTrendData`);
 *   2. "100% On Schedule", in green, always — because
 *      backend/routes/dashboard.py:69 returns the literal 100 with a TODO
 *      beside it;
 *   3. "$0" and "0%" wherever the backend said nothing at all, because
 *      thirteen `|| 0` fallbacks collapsed ABSENT into ZERO.
 *
 * The rule this module exists to hold is the one the rest of the frontend
 * already holds elsewhere:
 *
 *   MISSING IS NOT ZERO. MALFORMED IS NOT EMPTY.
 *   AND A NUMBER OMNΛS CANNOT MEASURE IS NOT A MEASUREMENT.
 *
 * Everything here is pure. It takes what the backend sent and decides what may
 * be claimed from it; it computes no construction fact of its own.
 */

/* ------------------------------------------------------------------ *
 * FACTS
 *
 * A fact is `{ established, value }`. `established: false` means the backend
 * did not state this — which is different from stating zero, and must render
 * differently. Every consumer below reads a fact, never a raw field.
 * ------------------------------------------------------------------ */

const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const isCount = (value) => Number.isInteger(value) && value >= 0;

/** NOT_ESTABLISHED is a value, not an absence of one. */
export const NOT_ESTABLISHED = Object.freeze({ established: false, value: null });

/**
 * Read one numeric field.
 *
 * `0` is established — a project count of zero is a fact about this tenant.
 * `null`, `undefined`, a missing key, a string and NaN are not: nothing was
 * said, and the screen must say so rather than pick a number.
 */
export const numberFact = (raw) =>
  isFiniteNumber(raw) ? { established: true, value: raw } : NOT_ESTABLISHED;

/**
 * A percentage, additionally range-checked.
 *
 * A backend that sends 412 for a percentage has a defect; rendering "412%"
 * would pass that defect on to an operator as though it were a reading.
 */
export const percentFact = (raw) => {
  const fact = numberFact(raw);
  if (!fact.established) return NOT_ESTABLISHED;
  if (fact.value < 0 || fact.value > 100) return NOT_ESTABLISHED;
  return fact;
};

/* ------------------------------------------------------------------ *
 * LABELS
 *
 * The wording is deliberate and shared: one phrase for "the backend did not
 * say", so an operator learns it once and reads it everywhere.
 * ------------------------------------------------------------------ */

export const NOT_ESTABLISHED_LABEL = 'Not established';

/** $1.2M / $480K / $940 / $0 — and never $0 for an absent figure. */
export const formatCurrency = (value) => {
  if (!isFiniteNumber(value)) return NOT_ESTABLISHED_LABEL;
  const abs = Math.abs(value);
  if (abs >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};

export const countLabel = (fact) =>
  fact && fact.established ? String(fact.value) : NOT_ESTABLISHED_LABEL;

export const currencyLabel = (fact) =>
  fact && fact.established ? formatCurrency(fact.value) : NOT_ESTABLISHED_LABEL;

export const percentLabel = (fact) =>
  fact && fact.established ? `${Math.round(fact.value)}%` : NOT_ESTABLISHED_LABEL;

/* ------------------------------------------------------------------ *
 * CONTRACT READERS
 *
 * The shape is decided ONCE, before anything reads it. An unreadable payload
 * is refused whole: rendering it as an empty tenant would be a claim that this
 * contractor has no projects, no budget and no work — which is exactly the
 * claim a failed request cannot support.
 * ------------------------------------------------------------------ */

const refusal = (reason) => ({ readable: false, reason, stats: null });

const malformedMeasurement = () => ({
  established: false,
  value: null,
  contract: 'malformed',
  reason: 'malformed_contract',
  basis: null,
  totalProjects: null,
  measuredProjects: null,
  excludedProjects: null,
});

const measurementCounts = (raw) => {
  const total = raw.total_projects;
  const measured = raw.measured_projects;
  const excluded = raw.excluded_projects;
  if (![total, measured, excluded].every(isCount)) return null;
  if (measured > total || excluded !== total - measured) return null;
  return { totalProjects: total, measuredProjects: measured, excludedProjects: excluded };
};

/**
 * Read a #92 structured measurement, or the old scalar only when the whole
 * structured key is absent. Presence with a bad shape is a new-backend defect,
 * not an invitation to promote the deprecated mirror back to authority.
 */
const readPortfolioMeasurement = (payload, structuredKey, valueKey, legacyKey, percent = false) => {
  if (!hasOwn(payload, structuredKey)) {
    const legacy = percent ? percentFact(payload[legacyKey]) : numberFact(payload[legacyKey]);
    return { ...legacy, contract: 'legacy', reason: null, basis: null };
  }

  const raw = payload[structuredKey];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return malformedMeasurement();
  const counts = measurementCounts(raw);
  const reasonValid = raw.reason === null || typeof raw.reason === 'string';
  const basisValid = typeof raw.basis === 'string' && raw.basis.length > 0;
  const summaryCountMatches = counts && isCount(payload.total_projects)
    && counts.totalProjects === payload.total_projects;
  if (
    typeof raw.established !== 'boolean'
    || !summaryCountMatches
    || !reasonValid
    || !basisValid
  ) {
    return malformedMeasurement();
  }

  const fact = percent ? percentFact(raw[valueKey]) : numberFact(raw[valueKey]);
  if (raw.established) {
    if (!fact.established || raw.reason !== null || counts.measuredProjects === 0) {
      return malformedMeasurement();
    }
    return {
      ...fact,
      contract: 'structured',
      reason: null,
      basis: raw.basis,
      ...counts,
    };
  }

  if (raw[valueKey] !== null || typeof raw.reason !== 'string' || raw.reason.length === 0) {
    return malformedMeasurement();
  }
  return {
    established: false,
    value: null,
    contract: 'structured',
    reason: raw.reason,
    basis: raw.basis,
    ...counts,
  };
};

const readScheduleMeasurement = (payload) => {
  if (!hasOwn(payload, 'schedule_adherence')) {
    return { established: false, value: null, contract: 'absent', reason: 'old_backend' };
  }
  const raw = payload.schedule_adherence;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return malformedMeasurement();
  if (typeof raw.established !== 'boolean') return malformedMeasurement();

  if (raw.established) {
    const fact = percentFact(raw.percent);
    if (!fact.established || raw.reason !== null || typeof raw.basis !== 'string' || !raw.basis) {
      return malformedMeasurement();
    }
    return { ...fact, contract: 'structured', reason: null, basis: raw.basis };
  }

  if (raw.percent !== null || typeof raw.reason !== 'string' || !raw.reason || raw.basis !== null) {
    return malformedMeasurement();
  }
  return {
    established: false,
    value: null,
    contract: 'structured',
    reason: raw.reason,
    basis: null,
  };
};

export const readDashboardStats = (payload) => {
  if (payload === null || payload === undefined) {
    return refusal('The dashboard summary was not returned. MALFORMED IS NOT EMPTY.');
  }
  if (typeof payload !== 'object' || Array.isArray(payload)) {
    return refusal('The dashboard summary is not an object. MALFORMED IS NOT EMPTY.');
  }
  return {
    readable: true,
    reason: null,
    stats: {
      totalProjects: numberFact(payload.total_projects),
      activeProjects: numberFact(payload.active_projects),
      completedProjects: numberFact(payload.completed_projects),
      totalBudget: readPortfolioMeasurement(
        payload,
        'budget_total',
        'value',
        'total_budget',
      ),
      totalSpent: readPortfolioMeasurement(
        payload,
        'actual_cost_total',
        'value',
        'total_spent',
      ),
      onBudgetPercent: readPortfolioMeasurement(
        payload,
        'budget_position',
        'percent',
        'on_budget_percentage',
        true,
      ),
      scheduleAdherence: readScheduleMeasurement(payload),
    },
  };
};

export const readCommandCenter = (payload) => {
  if (payload === null || payload === undefined) {
    return { readable: false, reason: 'The command centre was not returned.', center: null };
  }
  if (typeof payload !== 'object' || Array.isArray(payload)) {
    return {
      readable: false,
      reason: 'The command centre payload is not an object. MALFORMED IS NOT EMPTY.',
      center: null,
    };
  }
  /**
   * WHICH EMPTINESS IS WHICH.
   *
   * `get_command_center` builds these maps with
   * `{status: count for status, count in rows}` over a GROUP BY, and the key
   * is ALWAYS present in the response. So the two emptinesses are different
   * facts and must not be conflated:
   *
   *   the key is ABSENT          nothing was said        -> NOT ESTABLISHED
   *   the key is {}              the query found none    -> zero, and that
   *                                                        is a real answer
   *
   * A first draft of this returned NOT_ESTABLISHED for `{}` as well. That was
   * its own lie in the other direction — it would have printed "Not
   * established" against Procurement for every tenant that simply has no
   * procurement items yet. Mutation C4 is what caught it: the mutation
   * survived because no test pinned the line, and the reason no test pinned it
   * was that the line should not have existed.
   */
  const bucketTotal = (group) => {
    if (!group || typeof group !== 'object' || Array.isArray(group)) return NOT_ESTABLISHED;
    const values = Object.values(group).filter(isFiniteNumber);
    return { established: true, value: values.reduce((sum, v) => sum + v, 0) };
  };
  const bucketSum = (group, keys) => {
    if (!group || typeof group !== 'object' || Array.isArray(group)) return NOT_ESTABLISHED;
    const values = keys.map((k) => group[k]).filter(isFiniteNumber);
    return { established: true, value: values.reduce((sum, v) => sum + v, 0) };
  };
  return {
    readable: true,
    reason: null,
    center: {
      procurementTotal: bucketTotal(payload.procurement),
      procurementPending: bucketSum(payload.procurement, ['planned', 'ordered', 'shipped']),
      activityTotal: bucketTotal(payload.activity_statuses),
      activityComplete: bucketSum(payload.activity_statuses, ['complete', 'completed']),
      pendingApprovals: numberFact(payload.pending_approvals),
      documentCount: numberFact(payload.document_count),
    },
  };
};

/* ------------------------------------------------------------------ *
 * THE TWO THINGS OMNΛS IS NOT ENTITLED TO CLAIM
 * ------------------------------------------------------------------ */

/**
 * SCHEDULE ADHERENCE.
 *
 * The old backend returns `on_schedule_percentage=100` as a placeholder. It
 * is never read. The #92 backend adds a structured `schedule_adherence`
 * measurement whose current `not_computed` answer is an explicit refusal.
 *
 * Rendering the old field would put "100% On Schedule"
 * in a green success card for every tenant on every day, which is the single
 * most dangerous sentence this product could say: it asserts that nothing is
 * late.
 *
 * This is a REFUSAL, not a derivation. The frontend does not compute schedule
 * adherence, does not estimate it, and does not hide the gap — it states that
 * OMNΛS has not established it, and the backend ask names what would close it.
 * A future established structured measurement can render through this same
 * function without weakening the old-backend refusal.
 */
export const scheduleAdherence = (measurement) => {
  if (measurement && measurement.established) {
    return {
      established: true,
      value: measurement.value,
      headline: `${Math.round(measurement.value)}%`,
      detail: null,
      basis: measurement.basis,
    };
  }
  if (measurement && measurement.contract === 'malformed') {
    return {
      established: false,
      headline: 'Not established',
      detail: 'The schedule measurement returned by the backend could not be verified.',
    };
  }
  return {
    established: false,
    headline: 'Not established',
    detail:
      measurement && measurement.reason === 'not_computed'
        ? 'OMNΛS does not yet compute schedule adherence.'
        : 'OMNΛS has no verified schedule-adherence measurement. No legacy placeholder is shown.',
  };
};

/**
 * SPEND HISTORY.
 *
 * The page used to draw a "Budget vs Actual Spend" area chart from six months
 * of numbers written into the component. There is no series behind it:
 * `dashboardService` requests none, and `routes/dashboard.py:43-49` sums
 * `Project.budget` and `Project.actual_cost` into two scalars. A curve cannot
 * be drawn from two totals, so no curve is drawn.
 *
 * The absence is stated rather than left blank: a missing chart reads as a
 * loading failure, and an operator who thinks the chart broke will go looking
 * for it.
 */
export const spendHistory = () => ({
  held: false,
  headline: 'No spend history is held',
  detail:
    'OMNΛS records a budget and an actual cost per project. It does not record ' +
    'when spend occurred, so a budget-versus-actual curve over time cannot be ' +
    'drawn from what is stored.',
});

/**
 * BUDGET POSITION.
 *
 * The old backend's scalar is compatibility-only. The #92 backend's structured
 * `budget_position` owns the presentation whenever that key is present. The
 * project count remains a defensive version-skew guard because the old backend
 * reports 100 when there are no projects.
 *
 * A percentage of nothing is not a hundred per cent. It is nothing.
 */
export const budgetPosition = (stats) => {
  if (!stats) return { established: false, detail: 'The dashboard summary was not read.' };
  const { totalProjects, onBudgetPercent } = stats;
  if (!totalProjects.established) {
    return { established: false, detail: 'The project count was not established.' };
  }
  if (totalProjects.value === 0) {
    return {
      established: false,
      detail: 'There are no projects, so there is no budget position to report.',
    };
  }
  if (onBudgetPercent.contract === 'malformed') {
    return {
      established: false,
      detail: 'The budget-position measurement returned by the backend could not be verified.',
    };
  }
  if (!onBudgetPercent.established) {
    return {
      established: false,
      detail:
        onBudgetPercent.reason === 'no_projects'
          ? 'There are no projects, so there is no budget position to report.'
          : onBudgetPercent.reason === 'no_comparable_projects'
            ? 'No projects have both an established budget and actual cost, so there is no budget position to report.'
            : 'The backend did not establish a budget position.',
    };
  }
  return {
    established: true,
    value: onBudgetPercent.value,
    detail: coverageLabel(onBudgetPercent),
  };
};

/** Visible only when a measurement excludes part of the portfolio. */
export const coverageLabel = (fact) => {
  if (!fact || fact.contract !== 'structured') return null;
  if (!isCount(fact.totalProjects) || !isCount(fact.measuredProjects)) return null;
  if (!isCount(fact.excludedProjects) || fact.excludedProjects === 0) return null;
  return `${fact.measuredProjects} of ${fact.totalProjects} projects measured · ${fact.excludedProjects} excluded`;
};

export const measurementDetail = (fact, subject) => {
  if (!fact || fact.contract === 'legacy') return null;
  if (fact.contract === 'malformed') return `The ${subject} measurement could not be verified.`;
  if (fact.reason === 'no_projects') return 'There are no projects to measure.';
  if (fact.reason === 'no_established_budget_values') {
    return 'No project has an established budget.';
  }
  if (fact.reason === 'no_established_actual_cost_values') {
    return 'No project has an established actual cost.';
  }
  if (fact.reason === 'non_finite_aggregate') return `The ${subject} total could not be verified.`;
  if (!fact.established) return `The ${subject} total is not established.`;
  return coverageLabel(fact);
};

/* ------------------------------------------------------------------ *
 * PER-PROJECT PROGRESS
 * ------------------------------------------------------------------ */

/**
 * `percent_complete` is nullable on `Project`. A project nobody has assessed
 * is not 0% complete — it is unassessed, and a progress bar sitting at zero
 * says the crew has done nothing.
 */
export const projectProgress = (project) => {
  const fact = percentFact(project ? project.percent_complete : undefined);
  return {
    established: fact.established,
    value: fact.established ? fact.value : null,
    label: fact.established ? `${Math.round(fact.value)}%` : NOT_ESTABLISHED_LABEL,
  };
};
